import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plug, Unplug, Lock, Briefcase, Sparkles } from "lucide-react";
import { useTr } from "@/i18n/translator";
import { useAuth } from "@/context/AuthContext";
import { allowedNetworks } from "@/lib/planFeatures";

const NETWORKS = [
  { id: "twitter", label: "Twitter / X" },
  { id: "instagram", label: "Instagram", oauth: "meta" },
  { id: "facebook", label: "Facebook", oauth: "meta" },
  { id: "linkedin", label: "LinkedIn", oauth: "linkedin" },
  { id: "tiktok", label: "TikTok", oauth: "tiktok" },
];

export default function SocialAccounts() {
  const tr = useTr();
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [handles, setHandles] = useState({});
  const [activeBrandName, setActiveBrandName] = useState("");
  const [providers, setProviders] = useState({});

  const plan = user?.plan || "free";
  const allowed = new Set(allowedNetworks(plan));

  const load = () =>
    Promise.all([
      api.get("/accounts").then(({ data }) => setAccounts(data)),
      api.get("/brands").then(({ data }) => {
        const active = (data || []).find((b) => b.is_active) || (data || [])[0];
        setActiveBrandName(active?.brand_name || "");
      }),
      api.get("/oauth/providers").then(({ data }) => {
        const map = {};
        for (const p of data.providers || []) map[p.name] = p;
        setProviders(map);
      }).catch(() => {}),
    ]);
  useEffect(() => { load(); }, []);

  const connect = async (network) => {
    const handle = handles[network]?.trim();
    if (!handle) return toast.error(tr("Escribe tu usuario"));
    try {
      await api.post("/accounts/connect", { network, handle });
      toast.success(`Conectado ${network} (simulado)`);
      setHandles({ ...handles, [network]: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al conectar");
    }
  };
  const disconnect = async (id) => { await api.delete(`/accounts/${id}`); load(); };

  const startOAuth = async (providerName) => {
    try {
      const { data } = await api.post(`/oauth/${providerName}/authorize`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar OAuth");
    }
  };

  const getAcc = (n) => accounts.find((a) => a.network === n);

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ENLACES · REDES SOCIALES")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Cuentas conectadas")}</h1>
          <div className="font-mono-data text-sm text-[#5C5C5C] mt-2">
            {tr("Tu plan")} <b className="uppercase">{plan === "free" ? "inicial" : plan}</b> · {tr("permite")} <b>{allowed.size}</b> {allowed.size === 1 ? tr("red") : tr("redes")} {tr("por marca")}
          </div>
          <div className="mt-2 inline-flex items-center gap-2 bg-[#FFD600] border border-[#0A0A0A] px-3 py-1.5 font-mono-mark text-[10px]" data-testid="accounts-scope-banner">
            <Briefcase className="w-3 h-3" />
            {tr("GESTIONANDO MARCA")}: <b>{activeBrandName}</b>
          </div>
        </div>
        <div className={`border border-[#0A0A0A] px-3 py-2 font-mono-mark text-[10px] ${providers.meta?.configured ? "bg-[#16A34A] text-white" : "bg-[#FFD600]"}`}>
          {providers.meta?.configured ? "OAUTH META: ACTIVO" : "OAUTH META: PENDIENTE CONFIG"}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {NETWORKS.map((n) => {
          const acc = getAcc(n.id);
          const locked = !allowed.has(n.id);
          return (
            <div
              key={n.id}
              className={`bg-white border p-6 relative ${locked ? "border-dashed border-[#5C5C5C] opacity-70" : "border-[#0A0A0A]"}`}
              data-testid={`account-${n.id}${locked ? "-locked" : ""}`}
            >
              {locked && (
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#0A0A0A] text-[#FFD600] px-2 py-1 font-mono-mark text-[9px]">
                  <Lock className="w-3 h-3" /> {tr("BLOQUEADO")}
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-xl">{n.label}</div>
                {!locked && (
                  <span className={`font-mono-mark text-[10px] px-2 py-1 ${acc ? "bg-[#E53935] text-white" : "bg-[#E5E5E5]"}`}>
                    {acc ? "CONECTADA" : "DESCONECTADA"}
                  </span>
                )}
              </div>

              {locked ? (
                <div className="space-y-3">
                  <div className="font-mono-data text-sm text-[#5C5C5C]">
                    {tr("Esta red no está incluida en tu plan actual.")}
                  </div>
                  <Link
                    to="/dashboard/subscription"
                    className="block text-center font-mono-mark text-[10px] bg-[#E53935] text-white px-3 py-2 border border-[#E53935] hover-brutal"
                    data-testid={`account-upgrade-${n.id}`}
                  >
                    {tr("MEJORAR PLAN")} →
                  </Link>
                </div>
              ) : acc ? (
                <>
                  <div className="font-mono-data text-sm">@{acc.handle}</div>
                  <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-1">desde {new Date(acc.connected_at).toLocaleDateString("es-MX")}</div>
                  {acc.oauth_real && (
                    <div className="mt-1 inline-flex items-center gap-1 bg-[#16A34A] text-white px-2 py-0.5 font-mono-mark text-[9px]">
                      <Sparkles className="w-2.5 h-2.5" /> OAUTH REAL
                    </div>
                  )}
                  <button onClick={() => disconnect(acc.id)} className="mt-4 w-full font-mono-mark text-[10px] bg-white border border-[#E53935] text-[#E53935] px-3 py-2 flex items-center justify-center gap-2" data-testid={`account-disconnect-${n.id}`}>
                    <Unplug className="w-3 h-3" /> {tr("DESCONECTAR")}
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  {n.oauth && (
                    <button
                      onClick={() => startOAuth(n.oauth)}
                      disabled={!providers[n.oauth]?.configured}
                      className={`w-full font-mono-mark text-[10px] px-3 py-2 border-2 border-[#0A0A0A] flex items-center justify-center gap-2 ${
                        providers[n.oauth]?.configured
                          ? "bg-[#FFD600] hover-brutal"
                          : "bg-[#E5E5E5] text-[#5C5C5C] cursor-not-allowed"
                      }`}
                      data-testid={`account-oauth-${n.id}`}
                      title={providers[n.oauth]?.configured ? "Conectar con cuenta real" : "El admin debe configurar credenciales"}
                    >
                      <Sparkles className="w-3 h-3" />
                      {providers[n.oauth]?.configured ? tr("CONECTAR OFICIAL") : tr("PENDIENTE CONFIG")}
                    </button>
                  )}
                  <input
                    value={handles[n.id] || ""}
                    onChange={(e) => setHandles({ ...handles, [n.id]: e.target.value })}
                    placeholder="tu_usuario"
                    className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                    data-testid={`account-input-${n.id}`}
                  />
                  <button onClick={() => connect(n.id)} className="w-full font-mono-mark text-[10px] bg-white border border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 flex items-center justify-center gap-2" data-testid={`account-connect-${n.id}`}>
                    <Plug className="w-3 h-3" /> {tr("CONECTAR DEMO")}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
